/**
 * 구슬 겉모습으로 쓰는 열매 도안.
 *
 * 모든 도안은 단위 좌표계에 그린다: 중심이 (0, 0), 열매 본체의 반지름이 1.
 * 잎이나 꼭지는 1을 넘어가도 되지만 SPRITE_EXTENT 안에 들어와야 한다. 실제 픽셀 크기와
 * 스프라이트 캐시는 fruitSprites.ts 가 담당하므로 여기서는 크기를 신경 쓰지 않는다.
 *
 * 도안은 매 프레임 다시 그리지 않고 스프라이트로 한 번만 구워지므로, 난수를 쓰면 안 된다.
 * 씨앗 위치처럼 흩어져 보여야 하는 것도 좌표를 고정해 둔다
 */

const TAU = Math.PI * 2;

/** 스프라이트가 담아주는 단위 좌표 반경. 본체(1) 밖으로 나가는 잎·꼭지 여유분이다 */
export const SPRITE_EXTENT = 1.3;

export type FruitDef = {
  id: string;
  /** 사람이 읽는 이름. 지금은 코드 가독성 용도 */
  label: string;
  draw: (ctx: CanvasRenderingContext2D) => void;
};

function disc(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
}

/** 왼쪽 위에서 들어오는 광택. 열매를 입체로 보이게 하는 가장 값싼 수단 */
function gloss(ctx: CanvasRenderingContext2D, x = -0.38, y = -0.4, rx = 0.3, ry = 0.2, alpha = 0.45) {
  ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, -0.7, 0, TAU);
  ctx.fill();
}

/** 위로 뻗은 두 갈래 잎 */
function leaves(ctx: CanvasRenderingContext2D, color = '#3fa14a', y = -0.92) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(-0.3, y, 0.32, 0.14, -0.55, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(0.3, y, 0.32, 0.14, 0.55, 0, TAU);
  ctx.fill();
}

function stem(ctx: CanvasRenderingContext2D, topY = -1.22, color = '#7a5230', width = 0.14) {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, -0.75);
  ctx.lineTo(0.05, topY);
  ctx.stroke();
}

/** 알알이 뭉친 열매(라즈베리·포도). 좌표를 고정해 스프라이트가 늘 같게 나온다 */
function cluster(ctx: CanvasRenderingContext2D, cells: [number, number, number][], color: string, shade: string) {
  for (const [x, y, r] of cells) {
    disc(ctx, x, y, r, color);
    // 알마다 오른쪽 아래에 그늘을 넣어 알 경계가 보이게 한다
    ctx.fillStyle = shade;
    ctx.beginPath();
    ctx.arc(x + r * 0.22, y + r * 0.24, r * 0.62, 0, TAU);
    ctx.fill();
    disc(ctx, x - r * 0.24, y - r * 0.26, r * 0.34, 'rgba(255, 255, 255, 0.35)');
  }
}

export const FRUITS: FruitDef[] = [
  {
    id: 'strawberry',
    label: '딸기',
    draw: (ctx) => {
      // 아래로 뾰족한 하트형 몸통
      ctx.fillStyle = '#e63946';
      ctx.beginPath();
      ctx.moveTo(0, 1);
      ctx.bezierCurveTo(-1.02, 0.22, -0.86, -0.78, 0, -0.7);
      ctx.bezierCurveTo(0.86, -0.78, 1.02, 0.22, 0, 1);
      ctx.fill();
      // 씨앗
      ctx.fillStyle = '#ffe066';
      for (const [x, y] of [
        [-0.42, -0.3],
        [0.06, -0.42],
        [0.48, -0.24],
        [-0.52, 0.16],
        [-0.16, 0.06],
        [0.24, 0.14],
        [0.56, 0.2],
        [-0.28, 0.54],
        [0.16, 0.58],
        [-0.02, 0.28],
      ] as [number, number][]) {
        ctx.beginPath();
        ctx.ellipse(x, y, 0.075, 0.05, 0.4, 0, TAU);
        ctx.fill();
      }
      leaves(ctx, '#40b25a', -0.78);
      disc(ctx, 0, -0.74, 0.16, '#40b25a');
      stem(ctx, -1.08, '#3f8f4a', 0.12);
    },
  },
  {
    id: 'blueberry',
    label: '블루베리',
    draw: (ctx) => {
      disc(ctx, 0, 0, 1, '#4661b8');
      disc(ctx, 0.12, 0.14, 0.82, '#3a4f9b');
      // 꼭대기 꽃받침(별 모양 흠)
      ctx.fillStyle = '#2b3b78';
      ctx.beginPath();
      ctx.arc(0, -0.5, 0.34, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = '#6a83d0';
      ctx.lineWidth = 0.07;
      for (let i = 0; i < 5; i++) {
        const a = (TAU / 5) * i - Math.PI / 2;
        ctx.beginPath();
        ctx.moveTo(0, -0.5);
        ctx.lineTo(Math.cos(a) * 0.3, -0.5 + Math.sin(a) * 0.3);
        ctx.stroke();
      }
      gloss(ctx, -0.42, -0.02, 0.24, 0.16, 0.4);
    },
  },
  {
    id: 'raspberry',
    label: '라즈베리',
    draw: (ctx) => {
      cluster(
        ctx,
        [
          [-0.46, -0.3, 0.31],
          [0.02, -0.42, 0.31],
          [0.48, -0.28, 0.31],
          [-0.28, 0.16, 0.31],
          [0.24, 0.16, 0.31],
          [-0.02, 0.58, 0.31],
        ],
        '#d6336c',
        'rgba(140, 20, 60, 0.45)'
      );
      leaves(ctx, '#4caf50', -0.82);
      stem(ctx, -1.12, '#4b7a35', 0.11);
    },
  },
  {
    id: 'cherry',
    label: '체리',
    draw: (ctx) => {
      // 줄기 두 개가 위에서 만난다
      ctx.strokeStyle = '#4b7a35';
      ctx.lineWidth = 0.11;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(-0.4, -0.18);
      ctx.quadraticCurveTo(-0.24, -0.9, 0.06, -1.14);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0.44, -0.1);
      ctx.quadraticCurveTo(0.4, -0.8, 0.06, -1.14);
      ctx.stroke();
      disc(ctx, -0.4, 0.34, 0.55, '#c92a2a');
      disc(ctx, 0.44, 0.42, 0.5, '#e03131');
      disc(ctx, -0.55, 0.16, 0.16, 'rgba(255, 255, 255, 0.5)');
      disc(ctx, 0.3, 0.26, 0.13, 'rgba(255, 255, 255, 0.45)');
      leaves(ctx, '#51cf66', -1.06);
    },
  },
  {
    id: 'apple',
    label: '사과',
    draw: (ctx) => {
      // 두 덩이를 겹쳐 위가 살짝 들어간 사과 실루엣을 만든다
      ctx.fillStyle = '#e03131';
      ctx.beginPath();
      ctx.arc(-0.28, 0.1, 0.78, 0, TAU);
      ctx.arc(0.28, 0.1, 0.78, 0, TAU);
      ctx.fill();
      disc(ctx, 0.3, 0.16, 0.72, '#c92a2a');
      gloss(ctx, -0.44, -0.34, 0.26, 0.17);
      stem(ctx, -1.1);
      ctx.fillStyle = '#40b25a';
      ctx.beginPath();
      ctx.ellipse(0.36, -0.92, 0.32, 0.15, 0.5, 0, TAU);
      ctx.fill();
    },
  },
  {
    id: 'orange',
    label: '오렌지',
    draw: (ctx) => {
      disc(ctx, 0, 0, 1, '#fd7e14');
      disc(ctx, 0.1, 0.12, 0.86, '#f76707');
      // 껍질 결
      ctx.strokeStyle = 'rgba(255, 200, 120, 0.5)';
      ctx.lineWidth = 0.06;
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI / 4) * i;
        ctx.beginPath();
        ctx.ellipse(0, 0, 0.86, 0.3, a, 0, TAU);
        ctx.stroke();
      }
      gloss(ctx, -0.4, -0.42, 0.28, 0.18, 0.5);
      disc(ctx, 0, -0.96, 0.13, '#8a5a2b');
      ctx.fillStyle = '#40b25a';
      ctx.beginPath();
      ctx.ellipse(0.34, -1.0, 0.3, 0.13, 0.45, 0, TAU);
      ctx.fill();
    },
  },
  {
    id: 'grape',
    label: '포도',
    draw: (ctx) => {
      cluster(
        ctx,
        [
          [-0.5, -0.2, 0.3],
          [0.0, -0.34, 0.3],
          [0.5, -0.2, 0.3],
          [-0.26, 0.24, 0.3],
          [0.26, 0.24, 0.3],
          [0.0, 0.66, 0.3],
        ],
        '#9c36b5',
        'rgba(80, 20, 100, 0.45)'
      );
      stem(ctx, -1.16, '#5c8a3a', 0.12);
      ctx.fillStyle = '#51cf66';
      ctx.beginPath();
      ctx.ellipse(0.42, -0.98, 0.34, 0.15, 0.35, 0, TAU);
      ctx.fill();
    },
  },
  {
    id: 'lemon',
    label: '레몬',
    draw: (ctx) => {
      // 양 끝이 뾰족한 타원. 살짝 기울여야 레몬처럼 보인다
      ctx.save();
      ctx.rotate(-0.35);
      ctx.fillStyle = '#fcc419';
      ctx.beginPath();
      ctx.ellipse(0, 0, 1, 0.72, 0, 0, TAU);
      ctx.fill();
      ctx.fillStyle = '#f8b40a';
      ctx.beginPath();
      ctx.ellipse(0.1, 0.12, 0.9, 0.62, 0, 0, TAU);
      ctx.fill();
      // 꼭지 돌기
      disc(ctx, -1.0, 0, 0.14, '#e8a400');
      disc(ctx, 1.0, 0, 0.14, '#e8a400');
      gloss(ctx, -0.36, -0.28, 0.3, 0.14, 0.5);
      ctx.restore();
      leaves(ctx, '#51cf66', -0.98);
    },
  },
  {
    id: 'watermelon',
    label: '수박',
    draw: (ctx) => {
      disc(ctx, 0, 0, 1, '#2f9e44');
      // 줄무늬. 클리핑으로 몸통 밖을 잘라낸다
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, 1, 0, TAU);
      ctx.clip();
      ctx.strokeStyle = '#1a6b2a';
      ctx.lineWidth = 0.17;
      for (const x of [-0.62, -0.2, 0.22, 0.64]) {
        ctx.beginPath();
        ctx.moveTo(x, -1.1);
        ctx.quadraticCurveTo(x * 1.5, 0, x, 1.1);
        ctx.stroke();
      }
      ctx.restore();
      gloss(ctx, -0.42, -0.44, 0.26, 0.16, 0.35);
      stem(ctx, -1.14, '#5c8a3a', 0.13);
    },
  },
  {
    id: 'peach',
    label: '복숭아',
    draw: (ctx) => {
      ctx.fillStyle = '#ffa8a8';
      ctx.beginPath();
      ctx.arc(-0.16, 0.06, 0.86, 0, TAU);
      ctx.arc(0.16, 0.06, 0.86, 0, TAU);
      ctx.fill();
      disc(ctx, 0.34, 0.2, 0.72, '#ff8787');
      // 가운데 골
      ctx.strokeStyle = 'rgba(214, 90, 90, 0.65)';
      ctx.lineWidth = 0.08;
      ctx.beginPath();
      ctx.moveTo(0, -0.78);
      ctx.quadraticCurveTo(-0.2, 0.1, 0.02, 0.86);
      ctx.stroke();
      gloss(ctx, -0.46, -0.34, 0.24, 0.16, 0.55);
      leaves(ctx, '#51cf66', -0.86);
      stem(ctx, -1.06, '#7a5230', 0.11);
    },
  },
  {
    id: 'kiwi',
    label: '키위',
    draw: (ctx) => {
      disc(ctx, 0, 0, 1, '#8a6a4a');
      disc(ctx, 0, 0, 0.86, '#a3814f');
      disc(ctx, 0, 0, 0.74, '#94c92f');
      disc(ctx, 0, 0, 0.24, '#f1f8e3');
      // 씨앗을 속살 둘레에 두른다
      ctx.fillStyle = '#2b2b2b';
      for (let i = 0; i < 12; i++) {
        const a = (TAU / 12) * i;
        ctx.beginPath();
        ctx.ellipse(Math.cos(a) * 0.45, Math.sin(a) * 0.45, 0.075, 0.05, a, 0, TAU);
        ctx.fill();
      }
      // 속살의 결
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.lineWidth = 0.04;
      for (let i = 0; i < 12; i++) {
        const a = (TAU / 12) * i + TAU / 24;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * 0.26, Math.sin(a) * 0.26);
        ctx.lineTo(Math.cos(a) * 0.7, Math.sin(a) * 0.7);
        ctx.stroke();
      }
    },
  },
];

/** 열매를 무작위로 하나 고른다 */
export function randomFruitIndex(): number {
  return Math.floor(Math.random() * FRUITS.length);
}
